import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import '../CSS/CurrentSearch.css';

interface JobListing {
  id: number;
  icon: string;
  jobTitle: string;
  school: string;
  postingTime: string;
  company: string;
  location: string;
  description: string;
  applicationLink?: string;
  profileLink?: string;
  nominateLink?: string;
  status?: 'active' | 'under-development' | 'closed' | 'inactive';
}

interface JobFromAPI {
  jobId: number;
  uniAffil: string;
  uniLogo: string;
  jobTitle: string;
  jobLocation: string;
  applicationLink: string;
  jobProfile: string;
  nominateLink: string;
  jobDesc: string;
  createdDate: string;
  isAvailable?: boolean | number; // MySQL returns 0/1, but TypeScript types it as boolean
  jobStatus?: string; // 'Active', 'Inactive', 'Under Development', 'Closed'
}

const CurrentSearch: React.FC = () => {
  const [searchParams] = useSearchParams();
  const isFromAdmin = searchParams.get('fromAdmin') === 'true';
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedJob, setSelectedJob] = useState<JobListing | null>(null);
  const [expandedJobId, setExpandedJobId] = useState<number | null>(null);
  const [jobListings, setJobListings] = useState<JobListing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [iconAspectRatio, setIconAspectRatio] = useState<'square' | 'horizontal-rectangle' | 'vertical-rectangle' | null>(null);
  
  // Placeholder image path
  const PLACEHOLDER_IMAGE = '/assets/Web Version/image-thumbnail-image.png';

  // Helper function to convert database file path to API endpoint URL
  const getFileUrl = (filePath: string | null | undefined): string => {
    if (!filePath || !filePath.trim()) {
      return '';
    }
    // If path already starts with http, return as is
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }
    // Convert database path to API endpoint
    // Database path: Files/Icons/filename.png
    // API endpoint: /api/admin/create-job/file/Files/Icons/filename.png
    const cleanPath = filePath.startsWith('/') ? filePath.slice(1) : filePath;
    // Production API endpoint
    // return `/api/admin/create-job/file/${cleanPath}`;
    return `/api/admin/create-job/file/${cleanPath}`;
  };

  // Format date to readable string
  const formatPostingTime = (dateString: string): string => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInHours < 1) {
      return 'Posted less than an hour ago';
    } else if (diffInHours < 24) {
      return `Posted today, at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    } else if (diffInDays === 1) {
      return `Posted yesterday, at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    } else {
      return `Posted on ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}, at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    }
  };

  // Helper function to strip HTML tags from text for searching
  const stripHtmlTags = (html: string): string => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Fetch jobs from API
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setIsLoading(true);
        // Production API endpoint
        // const response = await fetch('/api/admin/create-job/available');
        // The /available endpoint now returns all jobs (not just available ones)
        // so we can filter out only "Inactive" jobs on the frontend
        const response = await fetch('/api/admin/create-job/available');
        const data = await response.json();

        if (data.success && data.data?.jobs) {
          // First filter by isAvailable: only show jobs where isAvailable === 1 (true)
          const availableJobs = data.data.jobs.filter((job: JobFromAPI) => {
            // Check isAvailable - handle both boolean and number (MySQL returns 0/1)
            // Convert to boolean: true if isAvailable is true or 1, false otherwise
            const isAvailableValue = job.isAvailable;
            const isAvailable = isAvailableValue === true || 
                               (typeof isAvailableValue === 'number' && isAvailableValue === 1) ||
                               (isAvailableValue !== undefined && isAvailableValue !== false && isAvailableValue !== 0);
            
            if (!isAvailable) {
              console.log('Filtering out job (isAvailable = 0):', { 
                jobId: job.jobId, 
                jobTitle: job.jobTitle, 
                isAvailable: job.isAvailable,
                isAvailableValue: isAvailableValue,
                jobStatus: job.jobStatus
              });
            }
            
            return isAvailable;
          });
          
          // Then map to JobListing format
          const jobs: JobListing[] = availableJobs
            .map((job: JobFromAPI) => {
            // Check if uniLogo is empty, null, or undefined
            const hasValidLogo = job.uniLogo && job.uniLogo.trim() !== '';
              
              // Determine job status for display purposes (status messages, button states)
              // Use jobStatus from database if available, otherwise infer from placeholders
              let status: 'active' | 'under-development' | 'closed' | 'inactive' = 'active';
              if (job.jobStatus) {
                // Use jobStatus from database, converting to lowercase with hyphens
                const dbStatus = job.jobStatus.toLowerCase().replace(/\s+/g, '-');
                if (dbStatus === 'inactive' || dbStatus === 'under-development' || dbStatus === 'closed' || dbStatus === 'active') {
                  status = dbStatus as 'active' | 'under-development' | 'closed' | 'inactive';
                } else {
                  // If jobStatus doesn't match expected values, log it and default to active
                  console.warn('Unexpected jobStatus value:', { jobId: job.jobId, jobStatus: job.jobStatus, dbStatus: dbStatus });
                  status = 'active';
                }
              } else {
                // Fallback: infer status from "[Placeholder]" values for backward compatibility
                if (job.applicationLink === '[Placeholder]' || job.nominateLink === '[Placeholder]') {
                  status = 'under-development';
                } else {
                  status = 'active';
                }
              }
            
            // Debug logging
            if (!hasValidLogo) {
              console.warn('Job missing uniLogo:', { jobId: job.jobId, jobTitle: job.jobTitle, job });
            }
            
            return {
              id: job.jobId,
              icon: hasValidLogo ? getFileUrl(job.uniLogo) : PLACEHOLDER_IMAGE,
              jobTitle: job.jobTitle,
              school: job.uniAffil,
              postingTime: formatPostingTime(job.createdDate),
              company: job.uniAffil,
              location: job.jobLocation,
              description: job.jobDesc || '',
              applicationLink: job.applicationLink || '',
              profileLink: job.jobProfile ? getFileUrl(job.jobProfile) : '',
                nominateLink: job.nominateLink || '',
                status: status
            };
          });

          setJobListings(jobs);
          
          // Set first job as selected by default
          if (jobs.length > 0) {
            setSelectedJob(jobs[0]);
          }
        } else {
          setJobListings([]);
        }
      } catch (error) {
        console.error('Error fetching jobs:', error);
        setJobListings([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobs();
  }, []);

  // Filter jobs based on search query - search through all available job fields
  const filteredJobs = searchQuery.trim() === '' 
    ? jobListings 
    : jobListings.filter(job => {
        const searchLower = searchQuery.toLowerCase();
        const descriptionText = stripHtmlTags(job.description).toLowerCase();
        
        return (
          job.jobTitle.toLowerCase().includes(searchLower) ||
          job.school.toLowerCase().includes(searchLower) ||
          job.location.toLowerCase().includes(searchLower) ||
          descriptionText.includes(searchLower)
        );
      });

  // Update selected job when filtered results change
  useEffect(() => {
    if (filteredJobs.length > 0) {
      // If current selected job is not in filtered results, select first filtered job
      const isSelectedJobInResults = selectedJob && filteredJobs.find(job => job.id === selectedJob.id);
      if (!isSelectedJobInResults) {
        setSelectedJob(filteredJobs[0]);
      }
    } else if (searchQuery.trim() !== '') {
      // If search query exists but no results, clear selection
      setSelectedJob(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredJobs, searchQuery]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Filter jobs based on search query - search through all fields
    const filtered = jobListings.filter(job => {
      const searchLower = searchQuery.toLowerCase();
      const descriptionText = stripHtmlTags(job.description).toLowerCase();
      
      return (
        job.jobTitle.toLowerCase().includes(searchLower) ||
        job.school.toLowerCase().includes(searchLower) ||
        job.location.toLowerCase().includes(searchLower) ||
        descriptionText.includes(searchLower)
      );
    });
    
    // If search returns results, select first one; otherwise clear selection
    if (filtered.length > 0) {
      setSelectedJob(filtered[0]);
    } else {
      setSelectedJob(null);
    }
  };

  const handleJobSelect = (job: JobListing) => {
    setSelectedJob(job);
    setIconAspectRatio(null); // Reset aspect ratio when selecting a new job
  };

  const handleJobToggle = (job: JobListing) => {
    if (expandedJobId === job.id) {
      setExpandedJobId(null);
    } else {
      setExpandedJobId(job.id);
      setSelectedJob(job);
      setIconAspectRatio(null); // Reset aspect ratio when selecting a new job
    }
  };

  return (
    <div className="current-search-page">
      <div 
        className="current-search-container"
        style={isFromAdmin ? { marginTop: '10vh' } : {}}
      >
        <form className="current-search-form" onSubmit={handleSearchSubmit}>
          <div className="current-search-bar">
            <input
              type="text"
              className="current-search-input"
              placeholder="Search for a keyword or university name"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <button type="submit" className="current-search-icon-button" aria-label="Search">
              <svg
                className="current-search-icon"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 19L13 13M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z"
                  stroke="#000000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </form>

        {isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>Loading jobs...</div>
        ) : searchQuery.trim() !== '' && filteredJobs.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            No jobs found matching your search. Try different keywords.
          </div>
        ) : filteredJobs.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            There are no jobs at this time. Please check back again at a later date.
          </div>
        ) : (
          <div className="current-search-content">
            {/* Desktop: Two-column layout */}
            {/* Left Column - Job Listings */}
            <div className="current-search-listings">
              {filteredJobs.map((job) => (
                <button
                  key={job.id}
                  className={`current-search-listing-item ${selectedJob?.id === job.id ? 'active' : ''}`}
                  onClick={() => handleJobSelect(job)}
                >
                  <div className="current-search-listing-content">
                    <div className="current-search-listing-title">{job.jobTitle}</div>
                    <div className="current-search-listing-school">{job.school}</div>
                    <div className="current-search-listing-time">{job.postingTime}</div>
                  </div>
                </button>
              ))}
            </div>

            {/* Right Column - Job Details */}
            <div className="current-search-details">
              {!selectedJob ? (
                <div style={{ padding: '2rem', textAlign: 'center' }}>Select a job to view details</div>
              ) : (
                <>
                  <div className="current-search-details-header">
                    <div className="current-search-details-header-left">
                      <div 
                        className="current-search-details-icon"
                        style={{
                          width: iconAspectRatio === 'square' ? '80px' : 
                                 iconAspectRatio === 'horizontal-rectangle' ? 'auto' : 
                                 iconAspectRatio === 'vertical-rectangle' ? '80px' : '80px',
                          height: iconAspectRatio === 'square' ? '80px' : 
                                  iconAspectRatio === 'horizontal-rectangle' ? '80px' : 
                                  iconAspectRatio === 'vertical-rectangle' ? 'auto' : '80px',
                          maxWidth: iconAspectRatio === 'horizontal-rectangle' ? '160px' : 
                                    iconAspectRatio === 'vertical-rectangle' ? '80px' : '80px',
                          minWidth: iconAspectRatio === 'horizontal-rectangle' ? '100px' : 
                                    iconAspectRatio === 'vertical-rectangle' ? '60px' : '80px',
                          maxHeight: iconAspectRatio === 'vertical-rectangle' ? '160px' : '80px',
                          minHeight: iconAspectRatio === 'vertical-rectangle' ? '100px' : '80px'
                        }}
                      >
                        <img 
                          src={selectedJob.icon} 
                          alt={selectedJob.company} 
                          onLoad={(e) => {
                            const img = e.target as HTMLImageElement;
                            const width = img.naturalWidth;
                            const height = img.naturalHeight;
                            if (width === height) {
                              setIconAspectRatio('square');
                            } else if (width > height) {
                              setIconAspectRatio('horizontal-rectangle');
                            } else {
                              setIconAspectRatio('vertical-rectangle');
                            }
                          }}
                          onError={(e) => {
                          (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                            setIconAspectRatio('square');
                          }}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            maxWidth: '100%',
                            maxHeight: '100%'
                          }}
                        />
                      </div>
                      <div className="current-search-details-company-info">
                        <div className="current-search-details-company-name">{selectedJob.company}</div>
                      </div>
                    </div>
                    <div className="current-search-details-timestamp">{selectedJob.postingTime}</div>
                  </div>

                  <div className="current-search-details-title">{selectedJob.jobTitle}</div>
                  <div className="current-search-details-company">{selectedJob.location}</div>

                  {/* Status message at the top for "Closed" and "Under Development" */}
                  {(selectedJob.status === 'under-development' || selectedJob.status === 'closed') && (
                    <div 
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '1rem',
                        fontWeight: 400,
                        color: selectedJob.status === 'under-development' ? '#FF0000' : '#000000',
                        border: '2px solid',
                        borderColor: selectedJob.status === 'under-development' ? '#FF0000' : '#000000',
                        padding: '0.75rem 1rem',
                        marginTop: '1rem',
                        marginBottom: '1rem',
                        textAlign: 'left',
                        borderRadius: '4px'
                      }}
                    >
                      {selectedJob.status === 'under-development' ? 'Under Development' : 'No Longer Accepting Applications'}
                    </div>
                  )}

                  <div className="current-search-details-buttons">
                    {selectedJob.applicationLink && (
                      <a 
                        href={(selectedJob.status === 'under-development' || selectedJob.status === 'closed') ? '#' : selectedJob.applicationLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          if (selectedJob.status === 'under-development' || selectedJob.status === 'closed') {
                            e.preventDefault();
                          }
                        }}
                        className="current-search-button current-search-button-primary"
                        style={{ 
                          textDecoration: 'none', 
                          display: 'inline-block',
                          opacity: (selectedJob.status === 'under-development' || selectedJob.status === 'closed') ? 0.5 : 1,
                          pointerEvents: (selectedJob.status === 'under-development' || selectedJob.status === 'closed') ? 'none' : 'auto',
                          cursor: (selectedJob.status === 'under-development' || selectedJob.status === 'closed') ? 'not-allowed' : 'pointer',
                          backgroundColor: (selectedJob.status === 'under-development' || selectedJob.status === 'closed') ? '#cccccc' : undefined,
                          color: (selectedJob.status === 'under-development' || selectedJob.status === 'closed') ? '#666666' : undefined
                        }}
                      >
                        Submit Application
                      </a>
                    )}
                      <a 
                      href={(selectedJob.status === 'under-development' || selectedJob.status === 'closed' || !selectedJob.profileLink || selectedJob.profileLink === '') ? '#' : selectedJob.profileLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      onClick={(e) => {
                        if (selectedJob.status === 'under-development' || selectedJob.status === 'closed' || !selectedJob.profileLink || selectedJob.profileLink === '') {
                          e.preventDefault();
                        }
                      }}
                        className="current-search-button current-search-button-secondary"
                      style={{ 
                        textDecoration: 'none', 
                        display: 'inline-block',
                        opacity: (selectedJob.status === 'under-development' || selectedJob.status === 'closed' || !selectedJob.profileLink || selectedJob.profileLink === '') ? 0.5 : 1,
                        pointerEvents: (selectedJob.status === 'under-development' || selectedJob.status === 'closed' || !selectedJob.profileLink || selectedJob.profileLink === '') ? 'none' : 'auto',
                        cursor: (selectedJob.status === 'under-development' || selectedJob.status === 'closed' || !selectedJob.profileLink || selectedJob.profileLink === '') ? 'not-allowed' : 'pointer',
                        backgroundColor: (selectedJob.status === 'under-development' || selectedJob.status === 'closed' || !selectedJob.profileLink || selectedJob.profileLink === '') ? '#cccccc' : undefined,
                        color: (selectedJob.status === 'under-development' || selectedJob.status === 'closed' || !selectedJob.profileLink || selectedJob.profileLink === '') ? '#666666' : undefined
                      }}
                      >
                        View Profile
                      </a>
                    {selectedJob.nominateLink && (
                      <a 
                        href={(selectedJob.status === 'under-development' || selectedJob.status === 'closed') ? '#' : selectedJob.nominateLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          if (selectedJob.status === 'under-development' || selectedJob.status === 'closed') {
                            e.preventDefault();
                          }
                        }}
                        className="current-search-button current-search-button-secondary"
                        style={{ 
                          textDecoration: 'none', 
                          display: 'inline-block',
                          opacity: (selectedJob.status === 'under-development' || selectedJob.status === 'closed') ? 0.5 : 1,
                          pointerEvents: (selectedJob.status === 'under-development' || selectedJob.status === 'closed') ? 'none' : 'auto',
                          cursor: (selectedJob.status === 'under-development' || selectedJob.status === 'closed') ? 'not-allowed' : 'pointer',
                          backgroundColor: (selectedJob.status === 'under-development' || selectedJob.status === 'closed') ? '#cccccc' : undefined,
                          color: (selectedJob.status === 'under-development' || selectedJob.status === 'closed') ? '#666666' : undefined
                        }}
                      >
                        Nominate
                      </a>
                    )}
                  </div>

                  <div 
                    className="current-search-details-description"
                    dangerouslySetInnerHTML={{ __html: selectedJob.description }}
                  />
                </>
              )}
            </div>

            {/* Mobile: Collapsible sections */}
            <div className="current-search-collapsible-list">
              {filteredJobs.map((job) => (
                <div
                  key={job.id}
                  className={`current-search-collapsible-item ${expandedJobId === job.id ? 'expanded' : ''}`}
                >
                  <button
                    className="current-search-collapsible-header"
                    onClick={() => handleJobToggle(job)}
                  >
                    <div className="current-search-collapsible-header-content">
                      <div className="current-search-collapsible-info">
                        <div className="current-search-collapsible-title">{job.jobTitle}</div>
                        <div className="current-search-collapsible-school">{job.school}</div>
                        <div className="current-search-collapsible-time">{job.postingTime}</div>
                      </div>
                    </div>
                    <div className="current-search-collapsible-arrow">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 7.5L10 12.5L15 7.5"
                          stroke="#666666"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </button>
                  <div className="current-search-collapsible-content">
                    <div className="current-search-details-title">{job.jobTitle}</div>
                    <div className="current-search-details-company">{job.location}</div>

                    {/* Status message at the top for "Closed" and "Under Development" */}
                    {(job.status === 'under-development' || job.status === 'closed') && (
                      <div 
                        style={{
                          fontFamily: 'Poppins, sans-serif',
                          fontSize: '1rem',
                          fontWeight: 400,
                          color: job.status === 'under-development' ? '#FF0000' : '#000000',
                          border: '2px solid',
                          borderColor: job.status === 'under-development' ? '#FF0000' : '#000000',
                          padding: '0.75rem 1rem',
                          marginTop: '1rem',
                          marginBottom: '1rem',
                          textAlign: 'left',
                          borderRadius: '4px'
                        }}
                      >
                        {job.status === 'under-development' ? 'Under Development' : 'No Longer Accepting Applications'}
                      </div>
                    )}

                    <div className="current-search-details-buttons">
                      {job.applicationLink && (
                        <a 
                          href={(job.status === 'under-development' || job.status === 'closed') ? '#' : job.applicationLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            if (job.status === 'under-development' || job.status === 'closed') {
                              e.preventDefault();
                            }
                          }}
                          className="current-search-button current-search-button-primary"
                          style={{ 
                            textDecoration: 'none', 
                            display: 'inline-block',
                            opacity: (job.status === 'under-development' || job.status === 'closed') ? 0.5 : 1,
                            pointerEvents: (job.status === 'under-development' || job.status === 'closed') ? 'none' : 'auto',
                            cursor: (job.status === 'under-development' || job.status === 'closed') ? 'not-allowed' : 'pointer',
                            backgroundColor: (job.status === 'under-development' || job.status === 'closed') ? '#cccccc' : undefined,
                            color: (job.status === 'under-development' || job.status === 'closed') ? '#666666' : undefined
                          }}
                        >
                          Submit Application
                        </a>
                      )}
                        <a 
                        href={(job.status === 'under-development' || job.status === 'closed' || !job.profileLink || job.profileLink === '') ? '#' : job.profileLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        onClick={(e) => {
                          if (job.status === 'under-development' || job.status === 'closed' || !job.profileLink || job.profileLink === '') {
                            e.preventDefault();
                          }
                        }}
                          className="current-search-button current-search-button-secondary"
                        style={{ 
                          textDecoration: 'none', 
                          display: 'inline-block',
                          opacity: (job.status === 'under-development' || job.status === 'closed' || !job.profileLink || job.profileLink === '') ? 0.5 : 1,
                          pointerEvents: (job.status === 'under-development' || job.status === 'closed' || !job.profileLink || job.profileLink === '') ? 'none' : 'auto',
                          cursor: (job.status === 'under-development' || job.status === 'closed' || !job.profileLink || job.profileLink === '') ? 'not-allowed' : 'pointer',
                          backgroundColor: (job.status === 'under-development' || job.status === 'closed' || !job.profileLink || job.profileLink === '') ? '#cccccc' : undefined,
                          color: (job.status === 'under-development' || job.status === 'closed' || !job.profileLink || job.profileLink === '') ? '#666666' : undefined
                        }}
                        >
                          View Profile
                        </a>
                      {job.nominateLink && (
                        <a 
                          href={(job.status === 'under-development' || job.status === 'closed') ? '#' : job.nominateLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            if (job.status === 'under-development' || job.status === 'closed') {
                              e.preventDefault();
                            }
                          }}
                          className="current-search-button current-search-button-secondary"
                          style={{ 
                            textDecoration: 'none', 
                            display: 'inline-block',
                            opacity: (job.status === 'under-development' || job.status === 'closed') ? 0.5 : 1,
                            pointerEvents: (job.status === 'under-development' || job.status === 'closed') ? 'none' : 'auto',
                            cursor: (job.status === 'under-development' || job.status === 'closed') ? 'not-allowed' : 'pointer',
                            backgroundColor: (job.status === 'under-development' || job.status === 'closed') ? '#cccccc' : undefined,
                            color: (job.status === 'under-development' || job.status === 'closed') ? '#666666' : undefined
                          }}
                        >
                          Nominate
                        </a>
                      )}
                    </div>

                    <div 
                      className="current-search-details-description"
                      dangerouslySetInnerHTML={{ __html: job.description }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrentSearch;

